import { useState } from 'react'
import { Modal } from '@/shared/ui/Modal'
import type { Contact } from '@/types/crm'

interface Props {
  companyName: string
  /** Null when adding, the existing contact when editing. */
  contact: Contact | null
  onClose: () => void
  onSave: (contact: Contact) => Promise<void>
}

export function ContactModal({ companyName, contact, onClose, onSave }: Props) {
  const [name, setName] = useState(contact?.name ?? '')
  const [role, setRole] = useState(contact?.role ?? '')
  const [email, setEmail] = useState(contact?.email ?? '')
  const [phone, setPhone] = useState(contact?.phone ?? '')
  const [skidaUser, setSkidaUser] = useState(contact?.knownSkidaUser ?? false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const editing = contact !== null
  const canSubmit = (name.trim().length > 0 || email.trim().length > 0) && !saving

  const submit = async () => {
    if (!canSubmit) return
    setSaving(true)
    setError(null)
    try {
      await onSave({
        name: name.trim(),
        role: role.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        knownSkidaUser: skidaUser,
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the contact.')
      setSaving(false)
    }
  }

  return (
    <Modal
      title={editing ? 'Edit contact' : 'Add contact'}
      subtitle={`${editing ? 'Update the' : 'Add a'} contact person for ${companyName}.`}
      width={420}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn--primary" onClick={submit} disabled={!canSubmit}>
            {saving ? 'Saving…' : editing ? 'Save contact' : 'Add contact'}
          </button>
        </>
      }
    >
      {error && <div className="modal__error">{error}</div>}

      <input
        className="input"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name"
        autoFocus
      />
      <input
        className="input"
        value={role}
        onChange={(e) => setRole(e.target.value)}
        placeholder="Role, e.g. Owner"
      />
      <div className="modal__grid2">
        <input
          className="input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
        />
        <input
          className="input"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Phone"
        />
      </div>
      <label className="modal__check">
        <input
          type="checkbox"
          checked={skidaUser}
          onChange={(e) => setSkidaUser(e.target.checked)}
        />
        This contact already uses Skida (consumer)
      </label>
      <div className="micro">
        The live lookup against the consumer <code>users</code> collection still takes precedence.
        Tick this only to record what you know about someone that lookup cannot see.
      </div>
    </Modal>
  )
}
